import { Button, Modal, Spinner, useToast } from "@design";
import { useQuery } from "@tanstack/react-query";
import { type TFunction } from "i18next";
import { ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAttachmentLimitsOptions, listDriveItemsOptions } from "@/api";
import { InlineNotice } from "@/components/InlineNotice";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { loadSharePointDoc, subfolderNote } from "./cloudFiles";
import { FolderFileList } from "./FolderFileList";
import { confirmsAll, FOLDER_PAGE, FOLDER_SHOWN, type FolderContents, folderContents } from "./folderFiles";
import type { CloudFile, FolderLink } from "./useAttachments";
import { useRejectionToast } from "./useRejectionToast";

/** Subfolder names named in the hint before "and N more". */
const NAMED_SUBFOLDERS = 5;

/**
 * Offers the files of a pasted SharePoint/OneDrive folder link. A few files are attached after one confirmation;
 * more are picked from a list. Subfolders aren't attached: the person is told, and the model gets their IDs.
 */
export function SharePointFolderDialog({
  link,
  onAttach,
}: Readonly<{ link: FolderLink | null; onAttach: (files: CloudFile[]) => void }>) {
  return link ? <FolderDialog key={link.key} link={link} onAttach={onAttach} /> : null;
}

/** Why a folder offers nothing to choose (said in a toast instead of a dialog). */
function nothingToOffer(contents: FolderContents | null, failed: boolean, room: number) {
  if (room === 0) return "noRoom";
  if (failed) return "listFailed";
  if (!contents) return null;
  if (contents.files.length === 0) return contents.subfolders.length > 0 ? "onlySubfolders" : "empty";
  return contents.usable.length === 0 ? "noneSupported" : null;
}

function FolderDialog({ link, onAttach }: Readonly<{ link: FolderLink; onAttach: (files: CloudFile[]) => void }>) {
  const { t } = useTranslation("chat");
  const toast = useToast();
  const { folder, max, done } = link;
  // The room when the folder was offered: other attachments settling meanwhile don't change the dialog under the person
  const [room] = useState(link.room);
  const listing = useQuery({
    ...listDriveItemsOptions({
      path: { drive_id: folder.drive_id ?? "" },
      query: { folder_id: folder.id, limit: FOLDER_PAGE },
    }),
    enabled: Boolean(folder.drive_id) && room > 0,
  });
  const { data: limits } = useQuery({ ...getAttachmentLimitsOptions(), staleTime: Number.POSITIVE_INFINITY });
  const explain = useRejectionToast(limits);
  const contents = listing.data ? folderContents(listing.data, limits) : null;
  const confirm = contents !== null && confirmsAll(contents, room);
  // null until the person changes the selection: a confirmation starts with every file ticked
  const [picked, setPicked] = useState<string[] | null>(null);
  const selected = picked ?? (confirm ? contents.usable.map((file) => file.item.id) : []);

  const problem = nothingToOffer(contents, !folder.drive_id || listing.isError, room);
  const reported = useRef(false);
  useEffect(() => {
    if (!problem || reported.current) return;
    reported.current = true;
    if (problem === "noneSupported")
      explain((contents?.files ?? []).map(({ item }) => ({ reason: "unsupported" as const, name: item.name })));
    else toast.error(t(`folder.${problem}`, { name: folder.name, max }));
    done();
  });
  if (problem) return null;

  const toggle = (id: string, on: boolean) =>
    setPicked(on ? [...selected, id] : selected.filter((other) => other !== id));
  const attach = () => {
    if (!contents) return;
    const note = subfolderNote(folder, contents.subfolders);
    const chosen = contents.usable.filter((file) => selected.includes(file.item.id)).map((file) => file.item);
    onAttach(
      chosen.map((item) => ({
        name: item.name,
        mimeType: item.mime_type,
        load: () => loadSharePointDoc(item).then((doc) => (note ? { ...doc, note } : doc)),
      })),
    );
    done();
  };

  return (
    <Modal
      open
      onClose={done}
      title={dialogTitle(t, folder.name, contents, confirm)}
      closeLabel={t("cloud.close")}
      actions={
        <>
          <Button variant="secondary" onClick={done}>
            {t("cloud.cancel")}
          </Button>
          <Button variant="primary" disabled={selected.length === 0} onClick={attach}>
            {t("cloud.attach", { count: selected.length })}
          </Button>
        </>
      }
    >
      {contents ? (
        <div className="flex flex-col gap-3">
          <FolderFileList files={contents.files} selected={selected} full={selected.length >= room} onToggle={toggle} />
          {!confirm && <RoomNote room={room} max={max} />}
          <MoreFilesNote contents={contents} webUrl={folder.web_url} />
          {contents.subfolders.length > 0 && <SubfolderNote names={contents.subfolders.map((sub) => sub.name)} />}
        </div>
      ) : (
        <div className="flex justify-center py-6">
          <Spinner size="md" aria-label={t("folder.loading")} />
        </div>
      )}
    </Modal>
  );
}

/** The folder's name while it loads, then what the dialog asks. */
function dialogTitle(t: TFunction<"chat">, name: string, contents: FolderContents | null, confirm: boolean) {
  if (!contents) return name;
  if (confirm) return t("folder.confirmTitle", { count: contents.usable.length, name });
  return t("folder.pickTitle", { name });
}

function RoomNote({ room, max }: Readonly<{ room: number; max: number }>) {
  const { t } = useTranslation("chat");
  return (
    <Text variant="caption" tone="secondary">
      {room >= max ? t("attachments.tooMany", { max }) : t("folder.roomLeft", { count: room })}
    </Text>
  );
}

function MoreFilesNote({ contents, webUrl }: Readonly<{ contents: FolderContents; webUrl: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "folder" });
  const { fileCount } = contents;
  if (fileCount !== null && fileCount <= FOLDER_SHOWN) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Text variant="caption" tone="secondary">
        {fileCount === null
          ? t("showingMany", { shown: FOLDER_SHOWN })
          : t("showing", { shown: FOLDER_SHOWN, count: fileCount })}
      </Text>
      <a
        href={webUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-content-primary underline underline-offset-2"
      >
        <Text as="span" variant="caption">
          {t("openInSharePoint")}
        </Text>
        <ExternalLink size={12} aria-hidden />
      </a>
    </div>
  );
}

function SubfolderNote({ names }: Readonly<{ names: string[] }>) {
  const { t } = useTranslation("chat", { keyPrefix: "folder" });
  const named = names.slice(0, NAMED_SUBFOLDERS);
  const rest = names.length - named.length;
  const list = formatList(rest > 0 ? [...named, t("moreSubfolders", { count: rest })] : named);
  return <InlineNotice>{t("subfolders", { count: names.length, names: list })}</InlineNotice>;
}
